import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import PrimaryButton from "../../components/common/PrimaryButton";
import FloatingView from "../../components/common/FloatingView";
import ProductService from "../../services/ProductService";
import SubscriptionService from "../../services/SubscriptionService";
import { API_STATUS } from "../../constants/apiStatusCode";
import { PlanAction } from "../../constants/planActions";
import { useNavigate } from "react-router-dom";
import EventService from "../../services/EventService";
import AccountService from "../../services/AccountService";
import UserService from "../../services/UserService";
import PaymentService from "../../services/PaymentService";
import { getAccessToken } from "../../utils/authStorage";
import UserSessionManager from "../../utils/UserSessionManager";
import { loadRazorpay, cleanupRazorpay } from "../../utils/razorpay";

const nurturixLogo = process.env.PUBLIC_URL + "/assets/images/logo.png";
const pulseWebLogo =
  process.env.PUBLIC_URL + "/assets/images/pulsewebstudio.png";

const ACTIVE_PLAN_STORAGE_KEY = "productsActivePlanTitle";

const ACCOUNT_PROVISION_SUCCESS_MESSAGE =
  "Account created successfully, you will need to sign-in again to complete the process";

const closePaymentVerificationModal = async () => {
  Swal.close();
  await new Promise((resolve) => setTimeout(resolve, 100));
};

const PAYMENT_VERIFICATION_COLOR = "rgb(204, 85, 0)";

const getPaymentVerificationModalHtml = () => `
  <div
        class="spinner-border"
        role="status"
        style="
          width: 2.25rem;
          height: 2.25rem;
          border-color: rgba(204, 85, 0, 0.28);
          border-right-color: ${PAYMENT_VERIFICATION_COLOR};
        "
      >
        <span class="visually-hidden">Loading...</span>
      </div>
`;

const normalizePlanTitle = (t) => (t || "").trim().toLowerCase();

const resolveActivePlanFromProfile = (userDetails) => {
  const session = UserSessionManager.getUserInfo();
  const activationPlanLabel = (
    userDetails?.activationPlan || session?.activationPlan || ""
  ).trim();
  const pricingPlanCode = (userDetails?.pricingPlanCode || "").trim();
  const isSubscriber = userDetails?.isSubscriber ?? session?.isSubscriber;

  const hasSubscription =
    Boolean(activationPlanLabel) ||
    (isSubscriber === true && Boolean(pricingPlanCode));

  if (!hasSubscription) {
    return { code: "", title: "" };
  }

  return {
    code: pricingPlanCode,
    title: activationPlanLabel,
  };
};

const Products = () => {
  const [products, setProducts] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(1);
  const [plans, setPlans] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [error, setError] = useState(null);
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [domainNameInput, setDomainNameInput] = useState("");
  const [companyNameInput, setCompanyNameInput] = useState("");
  const [isCompanyNameFromProfile, setIsCompanyNameFromProfile] = useState(false);
  const [domainCheckLoading, setDomainCheckLoading] = useState(false);
  const [domainCheckError, setDomainCheckError] = useState("");
  const [isDomainAvailable, setIsDomainAvailable] = useState(false);
  const [accountCreateLoading, setAccountCreateLoading] = useState(false);
  const [accountCreateError, setAccountCreateError] = useState("");
  const [accountProvisionSuccess, setAccountProvisionSuccess] = useState(false);
  const [activationPlan, setActivationPlan] = useState(null);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsAppNo: "",
    businessDetails: "",
    userTypeName: "",
    companyName: "",
  });
  /** Current subscription plan code (from profile after verified purchase). */
  const [activePlanCode, setActivePlanCode] = useState("");
  const [activePlanTitle, setActivePlanTitle] = useState("");
  const [orderId, setOrderId] = useState("");
  const navigate = useNavigate();
  const [showRenewal, setShowRenewal] = useState(
    () => UserSessionManager.getUserInfo()?.showRenewal ?? false
  );

  const canManageSubscription =
    userData.userTypeName !== "SuperAdmin" &&
    userData.userTypeName !== "Developer" &&
    !UserSessionManager.isDeveloper();

  const productMeta = {
    "PROD-NUX-0001": {
      logo: nurturixLogo,
      tagline: "Marketing Automation & Lead Management",
      productCategory: "nurturix",
    },
    "PROD-NUX-0002": {
      logo: pulseWebLogo,
      tagline: "Professional Website Design & Development",
      productCategory: "webDesign",
    },
  };

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      setError(null);
      try {
        const resp = await ProductService.getProducts();
        const respData = resp?.data || resp;
        const list = respData?.data || [];

        const enhanced = list.map((p) => {
          const meta = productMeta[p.productCode] || {};
          return {
            key: p.productCode,
            code: p.productCode,
            name: p.productName,
            tagline: p.description || meta.tagline || "",
            logo: meta.logo || nurturixLogo,
            productID: p.id,
            isActive: p.isActive,
            productCategory: meta.productCategory || "nurturix",
          };
        });

        setProducts(enhanced);
        const firstActive = enhanced.find((p) => p.isActive) || enhanced[0];
        if (firstActive) {
          setSelectedCode(firstActive.code);
          setSelectedProductId(firstActive.productID);
        }
      } catch (e) {
        setError("Unable to load products. Please try again later.");
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    if (!selectedCode) return;

    const fetchPlans = async () => {
      setIsLoadingPlans(true);
      setError(null);
      try {
        const resp =
          await SubscriptionService.getPricingPlanByProduct(selectedCode);
        const respData = resp?.data || resp;
        const list = respData?.data || [];

        const mappedPlans = list?.map((plan) => ({
          title: plan.title || "",
          cost: plan.cost || "",
          productCode: plan.productCode || "",
          term: plan.termMonths || 0,
          features: plan.features || [],
          pricingPlanCode: plan.pricingPlanCode || "",
        }));

        setPlans(mappedPlans);
      } catch (e) {
        setPlans([]);
        if (e.response?.data?.statusCode === API_STATUS.NOT_FOUND) {
          setError(e.response.data.message);
        } else {
          setError("Unable to load pricing plans. Please try again later.");
        }
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [selectedCode]);

  useEffect(() => {
    if (!getAccessToken()) return;

    (async () => {
      try {
        const response = await UserService.fetchUserProfile();
        console.log("User_Response", response);

        if (response.data?.success && response.data.data) {
          const userDetails = response.data.data;
          const profileCompanyName = (userDetails?.companyName || "").trim();
          const sanitizedProfileCompanyName =
            sanitizeCompanyName(profileCompanyName);
          const { code, title } = resolveActivePlanFromProfile(userDetails);
          setActivePlanCode(code);
          setActivePlanTitle(title);
          persistActivePlan(code, title);

          setUserData({
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            email: userDetails.email,
            whatsAppNo: userDetails.whatsAppNo,
            businessDetails: userDetails.businessDetails,
            companyName: profileCompanyName,
            userTypeName: userDetails.userTypeName,
          });
          setCompanyNameInput(profileCompanyName);
          setIsCompanyNameFromProfile(Boolean(profileCompanyName));
          if (sanitizedProfileCompanyName) {
            localStorage.setItem("companyName", sanitizedProfileCompanyName);
          } else {
            localStorage.removeItem("companyName");
          }
        } else {
          console.error("API did not return expected data format");
        }
      } catch (error) {
        console.error("Error fetching profile details:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!getAccessToken()) return;
    fetchRazorPay();
  }, []);

  const fetchRazorPay = async () => {
    try {
      const resp = await EventService.getRazorPayKey();
      if (resp) {
        const data = resp;
        setRazorpayKeyId(data?.data?.data?.razorpayKeyId || "");
      }
    } catch (err) {
      console.log("No existing Razorpay profile");
    }
  };

  const burntOrange = "#CC5500";
  const lightCardBg = "#FFF9F4";

  const getTodayISO = () => new Date().toISOString();

  const addMonthsToTodayISO = (months) => {
    const d = new Date();
    const m = Number(months);
    d.setMonth(d.getMonth() + (Number.isFinite(m) ? m : 0));
    return d.toISOString();
  };

  const sanitizeCompanyName = (value) =>
    (value || "").replace(/[^a-zA-Z0-9]/g, "");

  const persistActivePlan = (code, title = "") => {
    const c = (code || "").trim();
    const t = (title || "").trim();
    setActivePlanCode(c);
    if (t) {
      setActivePlanTitle(t);
    } else if (!c) {
      setActivePlanTitle("");
    }
    try {
      if (c) localStorage.setItem(ACTIVE_PLAN_STORAGE_KEY, c);
      else localStorage.removeItem(ACTIVE_PLAN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  };

  const isCurrentPlan = (plan) => {
    if (activePlanCode) {
      return (
        normalizePlanTitle(plan.pricingPlanCode) ===
        normalizePlanTitle(activePlanCode)
      );
    }
    if (activePlanTitle) {
      return normalizePlanTitle(plan.title) === normalizePlanTitle(activePlanTitle);
    }
    return false;
  };

  const getPlanAction = (plan, defaultAction) =>
    isCurrentPlan(plan) && showRenewal ? PlanAction.RENEW : defaultAction;

  const clearShowRenewalFlag = () => {
    UserSessionManager.updateShowRenewal(false);
    setShowRenewal(false);
  };

  const openDomainAvailabilityModal = (plan) => {
    setActivationPlan(plan);
    setDomainModalOpen(true);
    setDomainNameInput("");
    setDomainCheckLoading(false);
    setDomainCheckError("");
    setIsDomainAvailable(false);
    setAccountCreateLoading(false);
    setAccountCreateError("");
    setAccountProvisionSuccess(false);
  };

  const closeDomainModal = () => {
    setDomainModalOpen(false);
    setAccountProvisionSuccess(false);
  };

  const goToSignInAfterProvision = () => {
    closeDomainModal();
    navigate("/signin");
  };

  const handleCheckDomainAvailability = async () => {
    const domainName = (domainNameInput || "").trim();
    if (!domainName) {
      setDomainCheckError("Please enter a domain name.");
      return;
    }

    setDomainCheckLoading(true);
    setDomainCheckError("");
    setIsDomainAvailable(false);

    try {
      const resp = await AccountService.checkDomain({ domainName });
      const ok = !resp.data.data;
      if (!ok) {
        const msg =
          resp?.data?.message ||
          resp?.data?.data?.message ||
          "Domain is not available.";
        setDomainCheckError(msg);
        return;
      }
      setIsDomainAvailable(true);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to check domain availability. Please try again.";
      setDomainCheckError(msg);
    } finally {
      setDomainCheckLoading(false);
    }
  };

  const handleCreateAccountAndMigrate = async () => {
    const domainName = (domainNameInput || "").trim();
    const companyName = (companyNameInput || "").trim();
    const termMonths =
      Number(activationPlan?.termMonths ?? activationPlan?.term ?? 0) || 0;

    if (!companyName) {
      setAccountCreateError("Company name is required.");
      return;
    }
    if (!domainName) {
      setAccountCreateError("Domain name is required.");
      return;
    }
    if (!termMonths) {
      setAccountCreateError(
        "Invalid plan term; cannot calculate activate-to date.",
      );
      return;
    }

    setAccountCreateLoading(true);
    setAccountCreateError("");
    const sanitizedCompanyName = sanitizeCompanyName(companyName);
    localStorage.setItem("companyName", sanitizedCompanyName);

    try {
      const todayIso = getTodayISO();
      const activateToIso = addMonthsToTodayISO(termMonths);

      const createPayload = {
        accountName: companyName,
        accountDomainName: domainName,
        accountDatabaseName: sanitizedCompanyName,
        isMasterTenant: true,
        isActive: true,
        activeFrom: todayIso,
        activeTo: activateToIso,
        activePlan: activationPlan?.title || "",
        productCode: activationPlan?.productCode || "",
        productId: selectedProductId,
        pricingPlanCode: activationPlan?.pricingPlanCode || "",
        orderId: orderId,
      };
      const createResp = await AccountService.createAccount(createPayload);
      const createOk = createResp.data.success;
      if (!createOk) {
        throw new Error(
          createResp?.data?.message || "Failed to create account.",
        );
      }

      const migratePayload = { dbname: sanitizedCompanyName };
      const migrateResp = await AccountService.migrateDatabase(migratePayload);
      const migrateOk = migrateResp.data;
      if (migrateOk) {
        persistActivePlan(
          activationPlan?.pricingPlanCode || "",
          activationPlan?.title || "",
        );
        setAccountProvisionSuccess(true);
      } else {
        throw new Error(
          migrateResp?.data?.message || "Failed to migrate database.",
        );
      }
    } catch (e) {
      if (e?.response?.data?.statusCode === API_STATUS.CONFLICT) {
        persistActivePlan(
          activationPlan?.pricingPlanCode || "",
          activationPlan?.title || "",
        );
        setAccountProvisionSuccess(true);
      } else {
        var msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to create account and migrate database.";
        setAccountCreateError(msg);
      }
    } finally {
      setAccountCreateLoading(false);
    }
  };

  const handleSubmit = async (plan, action) => {
    if (!getAccessToken()) {
      navigate("/signin");
      return;
    }

    const paymentRequest = mapFormValuesToPaymentRequest(plan);
    try {
      const res = await PaymentService.createPricingOrder(paymentRequest);
      if (res.data.success) {
        const order = res.data.data;
        setOrderId(order.razorpayOrderId);
        openRazorpayCheckout(order, plan, action);
      }
    } catch (err) {
      console.error("Payment order error:", err);
      alert(err.response?.data?.message || "Error initiating payment");
    }
  };

  const openRazorpayCheckout = async (order, plan, action) => {
    const loaded = await loadRazorpay();
    if (!loaded || !window.Razorpay) {
      const SWEET_ALERT_CONFIRM_COLOR = 'rgb(208 125 63)';
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to load Razorpay SDK. Please check your internet connection.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
      return;
    }

    const options = {
      key: razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.razorpayOrderId,
      name: plan.title,
      description: "Pricing Plan Activation",

      prefill: {
        name: userData.firstName + " " + userData.lastName,
        email: userData.email,
        contact: userData.whatsAppNo,
      },
      handler: async function (response) {
        console.log("🔹 Razorpay Handler Response:", response);
        cleanupRazorpay();
        const orderIdToSend = response?.razorpay_order_id;
        // 3️⃣ Step 3: Verify Payment via backend
        Swal.fire({
          title: "Verifying Payment",
          html: `
    <div style="text-align:center">
      <p>Please wait while we verify your payment.</p>
      ${getPaymentVerificationModalHtml()}
    </div>
  `,
          width: 460,
          padding: 0,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          customClass: {
            popup: "payment-popup",
            title: "payment-title",
            htmlContainer: "payment-html-container",
          },
        });

        let verifyRes;
        let verifyError;
        try {
          verifyRes = await PaymentService.verifyPricingPayment({
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
        } catch (err) {
          verifyError = err;
        } finally {
          await closePaymentVerificationModal();
        }

        if (verifyError) {
          console.error(verifyError);
          const errorMsg = "Verification failed. Check logs.";
          alert(errorMsg);
          return;
        }

        try {
          const msg = verifyRes.data.data.isSuccess
            ? "Payment successful and registration completed!"
            : "Payment Verification Failed!";
          const isSuccess = verifyRes.data.data.isSuccess;
          if ((action === PlanAction.UPGRADE || action === PlanAction.RENEW) && isSuccess) {
            const termMonths = Number(plan?.term ?? 0) || 0;
            const sanitizedCompanyName = sanitizeCompanyName(
              userData.companyName,
            );
            try {
              const todayIso = getTodayISO();
              const activateToIso = addMonthsToTodayISO(termMonths);

              const createPayload = {
                accountName: userData.companyName,
                accountDomainName: sanitizedCompanyName,
                accountDatabaseName: sanitizedCompanyName,
                isMasterTenant: true,
                isActive: true,
                activeFrom: todayIso,
                activeTo: activateToIso,
                activePlan: plan?.title || "",
                productCode: plan?.productCode || "",
                productId: selectedProductId,
                pricingPlanCode: plan?.pricingPlanCode || "",
                orderId: orderIdToSend,
              };

              const createResp =
                await AccountService.createAccount(createPayload);
              const createOk = createResp.data.success;
              if (!createOk) {
                alert(createResp?.data?.message || "Failed to upgrade plan.");
              } else {
                persistActivePlan(plan?.pricingPlanCode, plan?.title);
                UserSessionManager.updateSession({
                  showRenewal: false,
                  subscriptionEndDate: addMonthsToTodayISO(termMonths),
                });
                setShowRenewal(false);
                alert(
                  action === PlanAction.RENEW
                    ? "Your plan has been renewed successfully."
                    : "Your plan has been upgraded successfully.",
                );
              }
            } catch (e) {
              if (e?.response?.data?.statusCode === API_STATUS.CONFLICT) {
                persistActivePlan(plan?.pricingPlanCode, plan?.title);
                UserSessionManager.updateSession({
                  showRenewal: false,
                  subscriptionEndDate: addMonthsToTodayISO(termMonths),
                });
                setShowRenewal(false);
                alert(
                  action === PlanAction.RENEW
                    ? "Your plan has been renewed successfully."
                    : "Your plan has been upgraded successfully.",
                );
              } else {
                alert(
                  e?.response?.data?.message ||
                    e?.message ||
                    "Failed to create account and migrate database.",
                );
              }
            } finally {
              setAccountCreateLoading(false);
            }
          } else {
            alert(msg);
            if (isSuccess) {
              UserSessionManager.updateSession({
                showRenewal: false,
                subscriptionEndDate: addMonthsToTodayISO(Number(plan?.term ?? 0)),
              });
              setShowRenewal(false);
              
              openDomainAvailabilityModal(plan);
            }
          }
        } catch (err) {
          console.error(err);
          const errorMsg = "Verification failed. Check logs.";
          alert(errorMsg);
        }
      },

      theme: {
        color: "#3399cc",
      },
      modal: {
        ondismiss: function () {
          cleanupRazorpay();
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const mapFormValuesToPaymentRequest = (plan) => {
    return {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      whatsAppNumber: userData.whatsAppNo,
      companyName: userData.companyName,
      amount: parseFloat(plan.cost * plan.term || 0),
      currency: "INR",
      paymentMethod: "netbanking",
      eventId: null,
      productId: selectedProductId,
      source: "Product",
    };
  };

  const selectedProduct = products.find((p) => p.code === selectedCode);

  return (
    <div className="py-5">
      <Container>
        {/* Product Tiles */}
        {error && (
          <Row className="mb-3">
            <Col className="text-center text-danger small">{error}</Col>
          </Row>
        )}
        <Row className="mb-4 justify-content-center gap-3">
          {isLoadingProducts && products.length === 0 ? (
            <Col className="text-center">Loading products...</Col>
          ) : (
            products.map((product) => (
              <Col
                key={product.key}
                xs={10}
                sm={5}
                md={4}
                lg={3}
                className="mb-3"
              >
                <Card
                  className={`h-100 shadow-sm text-center product-tile ${
                    selectedCode === product.code
                      ? "border-primary border-3"
                      : "border-light"
                  } clickable`}
                  style={{ cursor: "pointer", transition: "box-shadow 0.2s" }}
                  onClick={() => {
                    setSelectedCode(product.code);
                    setSelectedProductId(product.productID);
                  }}
                >
                  <Card.Body>
                    <img
                      src={product.logo}
                      alt={product.name + " Logo"}
                      style={{
                        maxHeight: "60px",
                        width: "auto",
                        marginBottom: 12,
                      }}
                    />
                    <h5 className="fw-bold mb-1">{product.name}</h5>
                    <div className="text-muted small mb-2">
                      {product.tagline}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>

        {/* Divider */}
        <div style={{ borderTop: "2px solid #eee", margin: "32px 0" }} />

        {/* Pricing/Details Section */}
        <Row className="justify-content-center">
          {(!selectedProduct || isLoadingPlans) && (
            <Col className="text-center mb-4">
              {isLoadingPlans
                ? "Loading plans..."
                : "Select a product to view plans."}
            </Col>
          )}
          {selectedProduct &&
            !isLoadingPlans &&
            plans.map((plan) => (
              <Col
                md={plans.length > 1 ? 4 : 8}
                className="mb-4"
                key={plan.title}
              >
                <Card className="h-100 border-0 shadow-sm text-center d-flex flex-column">
                  <Card.Body className="p-4 d-flex flex-column">
                    <div className="flex-grow-1">
                      <FloatingView className="mb-3">{plan.title}</FloatingView>
                      {plan.term && (
                        <div className="mb-2 text-muted">
                          per month + tax ({plan.term} months term, paid
                          upfront)
                        </div>
                      )}
                      {plan.cost && (
                        <div className="mb-2 fw-bold">Cost: {plan.cost}</div>
                      )}
                      {Array.isArray(plan.features) &&
                        plan.features.length > 0 && (
                          <>
                            <div className="fw-bold mb-2">What's included.</div>
                            <ul className="text-start mb-4">
                              {plan.features.map((f, i) => (
                                <li key={i} className="mb-2">
                                  ✔ {f}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                    </div>
                    <div className="mt-auto">
                      {canManageSubscription &&
                        (selectedProduct.productCategory === "webDesign" ? (
                          userData.userTypeName === "Admin" ? (
                            <PrimaryButton
                              className="w-50"
                              disabled={isCurrentPlan(plan) && !showRenewal}
                              onClick={() =>
                                !(isCurrentPlan(plan) && !showRenewal) &&
                                handleSubmit(plan, getPlanAction(plan, PlanAction.UPGRADE))
                              }
                            >
                              {isCurrentPlan(plan)
                                ? showRenewal
                                  ? PlanAction.RENEW
                                  : "Current plan"
                                : PlanAction.UPGRADE}
                            </PrimaryButton>
                          ) : (
                            <PrimaryButton
                              className="w-50"
                              disabled={isCurrentPlan(plan) && !showRenewal}
                              onClick={() =>
                                !(isCurrentPlan(plan) && !showRenewal) &&
                                handleSubmit(plan, getPlanAction(plan, PlanAction.ACTIVATE))
                              }
                            >
                              {isCurrentPlan(plan)
                                ? showRenewal
                                  ? PlanAction.RENEW
                                  : "Current plan"
                                : "Subscribe"}
                            </PrimaryButton>
                          )
                        ) : userData.userTypeName === "Admin" ? (
                          <PrimaryButton
                            className="w-100"
                            disabled={isCurrentPlan(plan) && !showRenewal}
                            onClick={() =>
                              !(isCurrentPlan(plan) && !showRenewal) &&
                              handleSubmit(plan, getPlanAction(plan, PlanAction.UPGRADE))
                            }
                          >
                            {isCurrentPlan(plan)
                              ? showRenewal
                                ? PlanAction.RENEW
                                : "Current plan"
                              : PlanAction.UPGRADE}
                          </PrimaryButton>
                        ) : (
                          <PrimaryButton
                            className="w-100"
                            disabled={isCurrentPlan(plan) && !showRenewal}
                            onClick={() =>
                              !(isCurrentPlan(plan) && !showRenewal) &&
                              handleSubmit(plan, getPlanAction(plan, PlanAction.ACTIVATE))
                            }
                          >
                            {isCurrentPlan(plan)
                              ? showRenewal
                                ? PlanAction.RENEW
                                : "Current plan"
                              : PlanAction.ACTIVATE}
                          </PrimaryButton>
                        ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
        </Row>
        <Modal
          show={domainModalOpen}
          onHide={closeDomainModal}
          centered
          backdrop="static"
        >
          <Modal.Header
            closeButton
            style={{ backgroundColor: burntOrange, color: "#fff" }}
          >
            <Modal.Title>Check Domain Availability</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {accountProvisionSuccess ? (
              <>
                <p className="mb-3 text-dark" role="status">
                  {ACCOUNT_PROVISION_SUCCESS_MESSAGE}
                </p>
                <div className="d-flex justify-content-end">
                  <Button variant="primary" onClick={goToSignInAfterProvision}>
                    Continue to sign in
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Company name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    value={companyNameInput}
                    onChange={(e) => {
                      setCompanyNameInput(e.target.value);
                      setAccountCreateError("");
                    }}
                    placeholder="Enter company name"
                    disabled={
                      isCompanyNameFromProfile ||
                      domainCheckLoading ||
                      accountCreateLoading
                    }
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Domain name</Form.Label>
                  <Form.Control
                    value={domainNameInput}
                    onChange={(e) => {
                      setDomainNameInput(e.target.value);
                      setIsDomainAvailable(false);
                      setDomainCheckError("");
                      setAccountCreateError("");
                    }}
                    placeholder="example.com"
                    disabled={domainCheckLoading || accountCreateLoading}
                  />
                </Form.Group>

                <div className="d-flex gap-2 flex-wrap mb-2">
                  <Button
                    variant="outline-dark"
                    style={{ borderColor: burntOrange, color: burntOrange }}
                    onClick={handleCheckDomainAvailability}
                    disabled={
                      domainCheckLoading ||
                      accountCreateLoading ||
                      !(companyNameInput || "").trim() ||
                      !(domainNameInput || "").trim()
                    }
                  >
                    {domainCheckLoading ? "Checking..." : "Check availability"}
                  </Button>

                  <Button
                    variant="primary"
                    onClick={handleCreateAccountAndMigrate}
                    disabled={!isDomainAvailable || accountCreateLoading}
                  >
                    {accountCreateLoading ? "Creating..." : "Create account"}
                  </Button>
                </div>

                {domainCheckError && (
                  <div className="text-danger small mb-2">
                    {domainCheckError}
                  </div>
                )}

                {isDomainAvailable && !domainCheckError && (
                  <div className="text-success small mb-2">
                    Domain is available. You can create your account.
                  </div>
                )}

                {accountCreateError && (
                  <div className="text-danger small mb-2">
                    {accountCreateError}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default Products;
